import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faSpinner, faExclamationTriangle, faHistory, faFilm, faTv, faUser, faChevronDown, faChevronUp, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { CommentsAPI } from '../../api';
import { formatLocalDateTime } from '../../utils/formatters';
import { Link, useNavigate } from 'react-router-dom';
import { MoviesAPI, ShowsAPI } from '../../api';

const CommentsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  svg {
    margin-right: 10px;
    color: var(--primary-light);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px;
  
  svg {
    color: var(--primary-light);
    font-size: 24px;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 30px;
  color: var(--text-dim);
`;

const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 10px 0;
  width: 100%;
`;

const CommentItem = styled.div`
  display: flex;
  flex-direction: column;
  padding: 15px;
  border-radius: 10px;
  background-color: rgba(26, 26, 26, 0.8);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background-color: rgba(42, 42, 42, 0.8);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  }
`;

const CommentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
  
  a {
    color: var(--primary-light);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const CommentInfo = styled.div`
  flex: 1;
`;

const CommentTitle = styled.div`
  color: #fff;
  font-weight: 500;
  
  span { 
    display: block;
    font-size: 16px; 
    margin-bottom: 4px;
  }
`;

const CommentMeta = styled.div`
  display: flex;
  align-items: center;
  color: var(--text-dim);
  font-size: 14px;
  flex-wrap: wrap;
  
  span {
    margin-right: 15px;
    display: flex;
    align-items: center;
    margin-bottom: 4px;
    
    svg {
      margin-right: 5px;
      font-size: 12px;
    }
  }
`;

const CommentTime = styled.span`
  font-size: 14px;
  color: var(--text-dim);
`;

const CommentContent = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-light);
  word-break: break-word;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  white-space: pre-wrap;
  
  a {
    color: var(--primary-light);
    text-decoration: underline;
    position: relative;
    padding-right: 18px;
    transition: all 0.2s ease;
    
    &:hover {
      color: var(--primary-color);
    }
    
    &.external-link::after {
      content: "";
      position: absolute;
      right: 0;
      top: 2px;
      width: 14px;
      height: 14px;
      background-color: currentColor;
      mask-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path d="M432,320H400a16,16,0,0,0-16,16V448H64V128H208a16,16,0,0,0,16-16V80a16,16,0,0,0-16-16H48A48,48,0,0,0,0,112V464a48,48,0,0,0,48,48H400a48,48,0,0,0,48-48V336A16,16,0,0,0,432,320ZM488,0h-128c-21.37,0-32.05,25.91-17,41l35.73,35.73L135,320.37a24,24,0,0,0,0,34L157.67,377a24,24,0,0,0,34,0L435.28,133.32,471,169c15,15,41,4.5,41-17V24A24,24,0,0,0,488,0Z"/></svg>');
      mask-repeat: no-repeat;
      mask-size: contain;
    }
  }
`;

const EmbeddedImage = styled.div`
  margin: 10px 0;
  max-width: 100%;
  
  img {
    max-width: 100%;
    max-height: 300px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .image-caption {
    font-size: 12px;
    color: var(--text-dim);
    margin-top: 5px;
    display: flex;
    align-items: center;
    
    svg {
      margin-right: 5px;
    }
    
    a {
      color: var(--primary-light);
      margin-left: 5px;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const LoadMoreButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  margin: 20px auto;
  display: block;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-3px);
    box-shadow: 0 7px 20px rgba(59, 130, 246, 0.4);
  }
  
  &:active {
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(220, 38, 38, 0.2);
  color: #ff4d4d;
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 10px;
  }
`;

const ToggleCommentsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(59, 130, 246, 0.1);
  color: var(--text-light);
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 500;
  cursor: pointer;
  margin: 10px 0;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.2);
    transform: translateY(-2px);
  }
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
`;

const AvatarContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  background-color: var(--background-light);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  border: 2px solid var(--primary-light);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    font-size: 20px;
    color: var(--text-dim);
  }
`;

const CommentRow = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`;

// Modal for external link warnings
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(3px);
`;

const ModalContent = styled.div`
  background-color: rgba(26, 26, 26, 0.95);
  border-radius: 12px;
  padding: 20px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(59, 130, 246, 0.3);
  
  h3 {
    color: var(--error-color);
    margin-bottom: 15px;
    display: flex;
    align-items: center;
    font-size: 18px;
    
    svg {
      margin-right: 10px;
    }
  }
  
  p {
    margin-bottom: 20px;
    color: var(--text-light);
    font-size: 14px;
    line-height: 1.5;
  }
  
  .link-display {
    background-color: rgba(0, 0, 0, 0.2);
    padding: 8px 12px;
    border-radius: 6px;
    margin-bottom: 15px;
    word-break: break-all;
    font-family: monospace;
    font-size: 13px;
    color: var(--primary-light);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .button-group {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }
  
  button {
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &.cancel {
      background-color: transparent;
      border: 1px solid rgba(255, 255, 255, 0.2);
      color: var(--text-light);
      
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }
    
    &.proceed {
      background-color: var(--primary-color);
      border: none;
      color: white;
      
      &:hover {
        background-color: var(--primary-light);
        transform: translateY(-1px);
      }
    }
  }
`;

const UserComments = ({ userId, isSelf = false }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [commentsVisible, setCommentsVisible] = useState(false);
  const [showLinkWarning, setShowLinkWarning] = useState(false);
  const [warningLink, setWarningLink] = useState('');
  const navigate = useNavigate();
  
  // Reset comments when userId changes
  useEffect(() => {
    if (commentsVisible) {
      // Reset and refetch when userId changes or visibility turns on
      fetchComments(true);
    }
  }, [userId, isSelf, commentsVisible]);
  
  const fetchComments = async (reset = false) => {
    if (reset) {
      setPage(1);
      setComments([]);
    }
    
    try {
      setLoading(true);
      setError(null);
      const limit = 10;
      const skip = reset ? 0 : comments.length;
      
      const params = {
        limit,
        skip
      };
      
      let response;
      if (isSelf) {
        response = await CommentsAPI.getCurrentUserComments(params);
      } else {
        response = await CommentsAPI.getUserComments(userId, params);
      }
      
      // Check if the response was successful
      if (!response.data || !response.data.success) {
        setError(response.data?.error || 'Failed to load comments. Please try again later.');
        return;
      }
      
      const { data } = response.data;
      
      // Process comments to ensure they have content titles
      const processedComments = (data?.comments || []).map(async comment => {
        try {
          const contentDetails = await fetchContentDetails(comment);
          return contentDetails;
        } catch (err) {
          console.error('Error processing comment:', err);
          // Return the original comment if we can't fetch details
          return comment;
        }
      });
      
      try {
        const commentsData = await Promise.all(processedComments);
        
        if (reset) {
          setComments(commentsData);
        } else {
          setComments(prev => [...prev, ...commentsData]);
        }
        
        // Check if there are more comments to load
        const total = data.total || 0;
        setHasMore(total > (reset ? commentsData.length : comments.length + commentsData.length));
        
        if (!reset) {
          setPage(prev => prev + 1);
        }
      } catch (err) {
        console.error('Error processing comments:', err);
        setError('Failed to process comments. Please try again later.');
      }
    } catch (err) {
      console.error('Error fetching user comments:', err);
      setError('Failed to load comments. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleComments = () => {
    if (!commentsVisible && comments.length === 0) {
      fetchComments(true);
    }
    setCommentsVisible(!commentsVisible);
  };
  
  // Helper function to fetch content details for a comment
  const fetchContentDetails = async (comment) => {
    if (!comment || !comment.content_type || !comment.content_id) return comment;
    
    try {
      let response;
      if (comment.content_type === 'movie') {
        response = await MoviesAPI.getById(comment.content_id);
      } else if (comment.content_type === 'show') {
        response = await ShowsAPI.getById(comment.content_id);
      }
      
      if (response && response.data && response.data.success) {
        const contentData = response.data.data;
        return {
          ...comment,
          content_title: contentData.title,
          contentData: contentData
        };
      }
      
      // Return the original comment if we couldn't get details
      return {
        ...comment,
        content_title: `${comment.content_type === 'movie' ? 'Movie' : 'Show'} #${comment.content_id}`
      };
    } catch (error) {
      console.error(`Error fetching ${comment.content_type} details:`, error);
      // Return the original comment with a fallback title
      return {
        ...comment,
        content_title: `${comment.content_type === 'movie' ? 'Movie' : 'Show'} #${comment.content_id}`
      };
    }
  };
  
  // Helper to get content title
  const getContentLink = (comment) => {
    if (!comment) return null;
    
    const { content_type, content_id } = comment;
    let path = '';
    let title = '';
    let contentType = '';
    
    if (content_type === 'movie') {
      path = `/movie/${content_id}`;
      contentType = 'Movie';
      // Try to get the title from various possible properties
      title = comment.content_title || comment.movieTitle || comment.title || '';
    } else if (content_type === 'show') {
      path = `/show/${content_id}`;
      contentType = 'TV Show';
      // Try to get the title from various possible properties
      title = comment.content_title || comment.showTitle || comment.title || '';
    }
    
    return { path, title, contentType };
  };
  
  // Handle loading more comments
  const handleLoadMore = () => {
    fetchComments();
  };
  
  // Navigate to the user profile (for avatar click)
  const handleAvatarClick = (comment) => {
    if (comment && comment.user && comment.user.username) {
      navigate(`/profile/${comment.user.username}`);
    }
  };
  
  // Process comment content to handle links and images
  const processCommentContent = (content) => {
    if (!content) return '';
    
    // Helper function to decode HTML entities
    const decodeHTMLEntities = (text) => {
      const textArea = document.createElement('textarea');
      textArea.innerHTML = text;
      return textArea.value;
    };
    
    // Decode HTML entities in the content
    const decodedContent = decodeHTMLEntities(content);
    
    // Regular expression to find URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // Regular expression to check if URL is an image
    const imageRegex = /\.(jpeg|jpg|gif|png|webp)(\?.*)?$/i;
    
    // Check if the content has URLs
    if (urlRegex.test(decodedContent)) {
      // Split content by URLs
      const parts = decodedContent.split(urlRegex);
      const matches = decodedContent.match(urlRegex) || [];
      
      return (
        <>
          {parts.map((part, i) => {
            // If this is an odd index, it's text content
            if (i % 2 === 0) {
              return part;
            } else {
              const url = matches[Math.floor(i / 2)];
              const isImage = imageRegex.test(url);
              const isExternalLink = !url.includes(window.location.hostname);
              
              // If it's an image URL, render the image
              if (isImage) {
                return (
                  <EmbeddedImage key={i}>
                    <img src={url} alt="User shared content" loading="lazy" />
                    <div className="image-caption">
                      <FontAwesomeIcon icon={faExternalLinkAlt} />
                      Image from:
                      <a 
                        href="#" 
                        onClick={(e) => handleLinkClick(e, url)}
                        title={url}
                      >
                        {url.length > 30 ? url.substring(0, 30) + '...' : url}
                      </a>
                    </div>
                  </EmbeddedImage>
                );
              }
              
              // Otherwise, render as a link
              return (
                <a 
                  key={i}
                  href="#" 
                  onClick={(e) => handleLinkClick(e, url)}
                  className={isExternalLink ? "external-link" : ""}
                >
                  {url.length > 30 ? url.substring(0, 30) + '...' : url}
                </a>
              );
            }
          })}
        </>
      );
    }
    
    // If no URLs, just return the decoded content
    return decodedContent;
  };
  
  // Handle external link clicks
  const handleLinkClick = (e, url) => {
    e.preventDefault();
    
    // Check if it's an external link
    if (!url.includes(window.location.hostname)) {
      setWarningLink(url);
      setShowLinkWarning(true);
    } else {
      // It's an internal link, navigate directly
      window.open(url, '_blank');
    }
  };
  
  // Handle proceeding to external link
  const handleProceedToLink = () => {
    if (warningLink) {
      window.open(warningLink, '_blank');
    }
    setShowLinkWarning(false);
  };
  
  // Add this helper function to extract comment content
  const extractCommentContent = (comment) => {
    let content = '';
    
    // If comment.content is an object with a content property
    if (typeof comment.content === 'object' && comment.content !== null) {
      content = comment.content.content || JSON.stringify(comment.content);
    } else if (typeof comment.content === 'string') {
      try {
        const parsedContent = JSON.parse(comment.content);
        if (parsedContent && typeof parsedContent === 'object' && parsedContent.content) {
          content = parsedContent.content;
        } else {
          content = comment.content;
        }
      } catch (e) {
        // It's just a regular string, not JSON
        content = comment.content;
      }
    } else {
      // Default case
      content = comment.content || '';
    }
    
    return content;
  };
  
  return (
    <CommentsContainer>
      <SectionTitle>
        <FontAwesomeIcon icon={faComments} />
        {isSelf ? 'Your Comments' : 'User Comments'}
      </SectionTitle>
      
      <ToggleCommentsButton onClick={handleToggleComments}>
        {commentsVisible ? 'Hide Comments' : 'Show Comments'}
        <FontAwesomeIcon icon={commentsVisible ? faChevronUp : faChevronDown} />
      </ToggleCommentsButton>
      
      {commentsVisible && (
        <>
          {error && (
            <ErrorMessage>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              {error}
            </ErrorMessage>
          )}
          
          {loading && comments.length === 0 ? (
            <LoadingContainer>
              <FontAwesomeIcon icon={faSpinner} />
            </LoadingContainer>
          ) : comments.length > 0 ? (
            <>
              <CommentsList>
                {comments.map(comment => {
                  const contentLink = getContentLink(comment);
                  const commentContent = extractCommentContent(comment);
                  
                  return (
                    <CommentItem key={comment._id}>
                      <CommentRow>
                        <AvatarContainer onClick={() => handleAvatarClick(comment)}>
                          {comment.user && comment.user.avatar ? (
                            <img src={`/avatars/${comment.user.avatar}`} alt="User avatar" />
                          ) : (
                            <FontAwesomeIcon icon={faUser} />
                          )}
                        </AvatarContainer>
                        <CommentInfo>
                          <CommentTitle>
                            <span>
                              <Link to={contentLink.path}>
                                {contentLink.title || `${contentLink.contentType} #${comment.content_id}`}
                              </Link>
                            </span>
                          </CommentTitle>
                          <CommentMeta>
                            <CommentTime>
                              <FontAwesomeIcon icon={contentLink.contentType === 'Movie' ? faFilm : faTv} /> {contentLink.contentType}
                            </CommentTime>
                            <CommentTime>
                              <FontAwesomeIcon icon={faHistory} /> {formatLocalDateTime(comment.createdAt)}
                            </CommentTime>
                          </CommentMeta>
                        </CommentInfo>
                      </CommentRow>
                      <CommentContent>
                        {processCommentContent(commentContent)}
                      </CommentContent>
                    </CommentItem>
                  );
                })}
              </CommentsList>
              
              {hasMore && (
                <LoadMoreButton 
                  onClick={handleLoadMore} 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '8px' }} />
                      Loading...
                    </>
                  ) : 'Load More'}
                </LoadMoreButton>
              )}
            </>
          ) : (
            <EmptyMessage>No comments yet.</EmptyMessage>
          )}
        </>
      )}
      
      {/* External Link Warning Modal */}
      {showLinkWarning && (
        <ModalOverlay>
          <ModalContent>
            <h3>
              <FontAwesomeIcon icon={faExclamationTriangle} />
              External Link Warning
            </h3>
            <p>You are about to visit an external website. We are not responsible for the content of external sites.</p>
            <div className="link-display">{warningLink}</div>
            <div className="button-group">
              <button className="cancel" onClick={() => setShowLinkWarning(false)}>Cancel</button>
              <button className="proceed" onClick={handleProceedToLink}>Proceed</button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </CommentsContainer>
  );
};

export default UserComments; 